import React from "react";
import { useLegal } from "@/lib/brandingStore";
import { Seo } from "@/lib/seo";

const Row = ({ label, value }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-3 py-3 border-b border-[#1F1F1F] last:border-0">
    <dt className="text-[11px] uppercase tracking-[0.22em] text-white/40 font-street">
      {label}
    </dt>
    <dd className="sm:col-span-2 text-sm text-white leading-relaxed">{value}</dd>
  </div>
);

const Missing = () => <em className="text-white/50">À compléter</em>;

const orMissing = (v) =>
  v && String(v).trim() !== "" ? <span>{v}</span> : <Missing />;

const MentionsLegales = () => {
  const legal = useLegal();

  return (
    <div
      data-testid="mentions-legales-root"
      className="mx-auto max-w-3xl px-4 sm:px-6 pt-12 sm:pt-16 pb-24 bg-[#0A0A0A] text-white"
    >
      <Seo
        title="Mentions légales LE 11"
        description="Informations légales de l'établissement LE 11 à Villiers-le-Bel."
      />
      <div className="text-xs uppercase tracking-[0.28em] text-[#C9A875] font-street">
        Informations légales
      </div>
      <h1 className="font-street mt-3 text-4xl sm:text-5xl lg:text-6xl tracking-wide leading-[1.02]">
        Mentions <span className="text-[#C9A875]">légales</span>
      </h1>

      <div className="mt-8 rounded-2xl border border-[#262626] bg-[#121212] p-5 sm:p-6">
        <dl>
          <Row label="Établissement" value={orMissing(legal.establishment_name)} />
          <Row label="Nom juridique / société" value={orMissing(legal.legal_name)} />
          <Row label="Forme juridique" value={orMissing(legal.legal_form)} />
          <Row label="SIRET" value={orMissing(legal.siret)} />
          <Row label="RCS" value={orMissing(legal.rcs)} />
          <Row
            label="Adresse"
            value={
              legal.address_street || legal.address_zip_city ? (
                <>
                  {legal.address_street}
                  {legal.address_street && legal.address_zip_city && <br />}
                  {legal.address_zip_city}
                </>
              ) : (
                <Missing />
              )
            }
          />
          <Row
            label="Téléphone"
            value={
              legal.phone ? (
                <a
                  href={`tel:${legal.phone.replace(/\s+/g, "")}`}
                  className="hover:text-[#F5EFE6] transition-colors"
                >
                  {legal.phone}
                </a>
              ) : (
                <Missing />
              )
            }
          />
          <Row
            label="Email"
            value={
              legal.email ? (
                <a
                  href={`mailto:${legal.email}`}
                  className="hover:text-[#F5EFE6] transition-colors"
                >
                  {legal.email}
                </a>
              ) : (
                <Missing />
              )
            }
          />
          <Row label="Nom de domaine" value={orMissing(legal.domain)} />
          <Row label="Responsable de publication" value={orMissing(legal.publication_director)} />
          <Row label="Hébergeur" value={orMissing(legal.host_provider)} />
          <Row
            label="Email de contact"
            value={
              legal.contact_email ? (
                <a
                  href={`mailto:${legal.contact_email}`}
                  className="hover:text-[#F5EFE6] transition-colors"
                >
                  {legal.contact_email}
                </a>
              ) : (
                <Missing />
              )
            }
          />
          {legal.additional_notice && legal.additional_notice.trim() !== "" && (
            <Row
              label="Informations complémentaires"
              value={
                <span className="whitespace-pre-wrap">{legal.additional_notice}</span>
              }
            />
          )}
        </dl>
      </div>

      <p className="mt-5 text-[11px] italic text-white/45 leading-relaxed">
        Les informations marquées « À compléter » seront mises à jour par
        l'établissement avant mise en ligne définitive.
      </p>
    </div>
  );
};

export default MentionsLegales;
